import { Table, type TableColumnDefinition } from "$components/table";
import useCurrencyFormat from "$routes/price-list/_lib/currencies";
import { Checkbox, Chip, makeStyles, styled } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import React from "react";
import { useIntl } from "react-intl";
import { useRevenueDepartmentsQuery } from "../__generated__/page.gql";
import { InlineActiveCell } from "./inline-active-cell";
import { InlinePriceCell } from "./inline-price-cell";
import { InlineRevenueDepartmentCell } from "./inline-revenue-department-cell";
import type { PriceListRow } from "./price-list-row";
import type { RevenueDepartmentOption } from "./use-pending-price-edits";

type RowHandlers = {
  isEditable: (row: PriceListRow) => boolean;
  isSelected: (rowId: string) => boolean;
  isChanged: (rowId: string) => boolean;
  onToggleSelected: (row: PriceListRow) => void;
  onEditPrice: (row: PriceListRow, valueInPence: number) => void;
  onEditActive: (row: PriceListRow, active: boolean) => void;
  onEditRevenueDepartment: (
    row: PriceListRow,
    department: RevenueDepartmentOption,
  ) => void;
};

type Props = {
  rows: PriceListRow[];
  overriddenPriceIds?: Set<string>;
  handlers: RowHandlers;
} & Partial<React.ComponentProps<typeof Table>>;

const NameWithBadge = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

// The select column: cell has zero padding and the checkbox has zero
// visual padding too. A11y comes from the surrounding label, which fills
// the whole cell and toggles the input natively — so the entire cell
// is a touch target without adding visible padding around the glyph.
// `min-height: 2.75rem` keeps the tap area >= WCAG 2.5.5 (44 CSS px)
// when the user hasn't scaled text, and grows with their text scale.
const SelectCellLabel = styled("label")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  minHeight: "2.75rem",
  cursor: "pointer",
});

const SelectCellSpacer = styled("div")({
  width: "100%",
  height: "100%",
  minHeight: "2.75rem",
});

// MUI v4's TableCell sizeSmall keeps 16px left / 24px right horizontal
// padding (the 24px reserves space for an MUI sort-arrow icon — we don't
// sort, so it's dead weight). Compress to a tighter all-round value so
// `dense` actually looks dense. The first cell (the select column) goes
// to zero padding so the label-based hit area can fill it edge-to-edge.
//
// We also override the inline pixel widths the table component sets on
// the sticky cells (it requires numeric `width` for stable
// `position: sticky`). Two reasons:
//   1. Convert px → rem so the sticky columns scale with the user's
//      preferred text size (WCAG 1.4.4 Resize Text).
//   2. Cap the name column at `40vw - select width` so the frozen left
//      rail never claims more than 40% of the viewport — keeps the
//      scrollable region usable on the narrowest phones.
// The second-cell `left` override re-aligns the sticky offset to the
// actual rendered select width (the table computes it from the numeric
// prop, which drifts from rem at non-default text scales).
const STICKY_SELECT_WIDTH = "2.25rem"; // 36px at default root font-size
const STICKY_NAME_MAX_WIDTH = `min(10rem, calc(40vw - ${STICKY_SELECT_WIDTH}))`;

const DenseTableScope = styled("div")(({ theme }) => ({
  "& .MuiTableCell-sizeSmall": {
    padding: theme.spacing(0.5, 1),
    "&:last-child": { paddingRight: theme.spacing(1) },
    "&:first-child:not(.MuiTablePagination-root)": { padding: 0 },
  },
  "& .MuiCheckbox-root": {
    padding: 0,
  },
  // Exclude the pagination footer cell — it's also a first-child
  // `MuiTableCell`, and the select-column width would otherwise crush the
  // whole pagination toolbar into a ~36px sliver.
  "& .MuiTableRow-root > .MuiTableCell-root:first-child:not(.MuiTablePagination-root)":
    {
      width: `${STICKY_SELECT_WIDTH} !important`,
    },
  "& .MuiTableRow-root > .MuiTableCell-root:nth-child(2)": {
    width: `${STICKY_NAME_MAX_WIDTH} !important`,
    left: `${STICKY_SELECT_WIDTH} !important`,
  },
  // Override the inline minWidth set by the table component so the floor
  // scales with the user's text size too — 47rem ≈ 752px at default
  // root font-size, matching the numeric prop below.
  "& .MuiTable-root": {
    minWidth: "47rem !important",
  },
}));

const PriceListTable: FC<Props> = ({
  rows,
  overriddenPriceIds,
  handlers,
  ...delegateProps
}) => {
  const { formatMessage } = useIntl();
  const currency = useCurrencyFormat();
  const formattedCurrency = currency
    ?.formatToParts(0)
    .find((part) => part.type === "currency")?.value;

  // Lifted from the per-row cell so the query fires once per page instead
  // of once per row — large lists were creating dozens of Apollo observers.
  const { data: revenueDepartmentsData } = useRevenueDepartmentsQuery();
  const revenueDepartmentOptions: readonly RevenueDepartmentOption[] =
    revenueDepartmentsData?.priceListV2.revenueDepartments.edges ?? [];

  const columns: TableColumnDefinition[] = [
    {
      field: "__select",
      // Mobile-first: 36px is wide enough to seat the 18px checkbox glyph
      // with ~9px slack each side, while staying as narrow as practical on
      // small viewports. Tap target is the full cell area via the label.
      width: 36,
      isSticky: true,
      headerName: "",
      renderCell: (_value, _index, _col, rowDef) => {
        const r = rowDef as unknown as PriceListRow;
        const editable = handlers.isEditable(r);
        if (!editable) {
          return <SelectCellSpacer aria-hidden="true" />;
        }
        return (
          <SelectCellLabel onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="small"
              color="primary"
              checked={handlers.isSelected(r.id)}
              onChange={() => handlers.onToggleSelected(r)}
              inputProps={{
                "aria-label": formatMessage({
                  id: "priceList.row.select",
                  defaultMessage: "Select row",
                }),
                // biome-ignore lint/suspicious/noExplicitAny: data-testid is not in MUI v4's InputHTMLAttributes typing
                ["data-testid" as any]: `priceList.row.${r.id}.select`,
              }}
            />
          </SelectCellLabel>
        );
      },
    },
    {
      field: "name",
      width: 160,
      isSticky: true,
      headerName: formatMessage({
        id: "priceList.landingPage.column.name",
        defaultMessage: "Item",
      }),
      renderCell: (value, _index, _col, row) => {
        const r = row as unknown as PriceListRow;
        const hasOverride = overriddenPriceIds?.has(r.id) ?? false;
        return (
          <NameWithBadge>
            <span>{value as string}</span>
            {hasOverride && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={formatMessage({
                  id: "priceList.landingPage.badge.hasFacilityOverride",
                  defaultMessage: "Has facility override",
                })}
                data-testid={`priceList.row.${r.id}.hasOverrideBadge`}
              />
            )}
          </NameWithBadge>
        );
      },
    },
    {
      field: "origin",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.landingPage.column.origin",
        defaultMessage: "Category",
      }),
      renderCell: (value) => (
        <p style={{ textTransform: "capitalize" }}>{value.toLowerCase()}</p>
      ),
    },
    {
      field: "price",
      flex: 2,
      headerName: `${formatMessage({
        id: "priceList.landingPage.column.price",
        defaultMessage: "Price",
      })} (${formattedCurrency})`,
      renderCell: (_value, _index, _col, row) => {
        const r = row as unknown as PriceListRow;
        return (
          <InlinePriceCell
            rowId={r.id}
            valueInPence={r.price}
            onChange={(next) => handlers.onEditPrice(r, next)}
            disabled={!handlers.isEditable(r)}
          />
        );
      },
    },
    {
      field: "tariff",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.landingPage.column.tariff",
        defaultMessage: "Price Type",
      }),
      renderCell: (value) => <p>{value.name}</p>,
    },
    {
      field: "revenueDepartment",
      // Dept names ("Diagnostic Imaging", "Outpatient Pharmacy") are the
      // longest text in the row — give them an extra flex unit so they
      // don't truncate when minTableWidth is engaged.
      flex: 3,
      headerName: formatMessage({
        id: "priceList.landingPage.column.revenueDepartment",
        defaultMessage: "Revenue Department",
      }),
      renderCell: (_value, _index, _col, row) => {
        const r = row as unknown as PriceListRow;
        return (
          <InlineRevenueDepartmentCell
            rowId={r.id}
            selectedId={r.revenueDepartment.id}
            selectedName={r.revenueDepartment.name}
            options={revenueDepartmentOptions}
            onChange={(next) => handlers.onEditRevenueDepartment(r, next)}
            disabled={!handlers.isEditable(r)}
          />
        );
      },
    },
    {
      field: "owner",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.landingPage.column.owner",
        defaultMessage: "Managed By",
      }),
      renderCell: (value) => (
        <p style={{ textTransform: "capitalize" }}>{value.toLowerCase()}</p>
      ),
    },
    {
      field: "active",
      flex: 1,
      headerName: formatMessage({
        id: "priceList.landingPage.column.active",
        defaultMessage: "Active",
      }),
      renderCell: (_value, _index, _col, row) => {
        const r = row as unknown as PriceListRow;
        return (
          <InlineActiveCell
            rowId={r.id}
            active={r.active}
            onChange={(next) => handlers.onEditActive(r, next)}
            disabled={!handlers.isEditable(r)}
          />
        );
      },
    },
  ];

  const rowStateClasses = useRowStateClasses();
  const rowClass = (row: { id: string | number }) => {
    const r = row as unknown as PriceListRow;
    const id = String(r.id);
    if (!handlers.isEditable(r)) return rowStateClasses.orgRow;
    if (handlers.isChanged(id)) return rowStateClasses.changed;
    if (handlers.isSelected(id)) return rowStateClasses.selected;
    return "";
  };

  return (
    <DenseTableScope>
      <Table
        dataTestId="priceList.table"
        columns={columns}
        rows={rows}
        minTableWidth={750}
        {...delegateProps}
        classes={{ rowClass }}
      />
    </DenseTableScope>
  );
};

// Row tint scheme — three distinct visual cues:
// - selected (checked, no edits yet): neutral warm hover tint
// - changed (has pending edits): light info-blue tint — encodes "pending"
//   on a different hue from disabled so the two don't compete on
//   intensity alone (they used to be stacked greys)
// - orgRow (not editable by this user): light grey, lighter than the
//   default cell divider so row dividers stay visible
const useRowStateClasses = makeStyles((theme) => ({
  selected: {
    backgroundColor: theme.palette.action.hover,
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  changed: {
    backgroundColor: alpha(theme.palette.info.main, 0.12),
    "&:hover": { backgroundColor: alpha(theme.palette.info.main, 0.16) },
    // The input keeps its default grey outline at low contrast on the
    // light-blue row tint; lift it with a deeper blue fill so the
    // edited field reads clearly against its row.
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.info.main, 0.24),
    },
  },
  orgRow: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    "&:hover": { backgroundColor: theme.palette.grey[100] },
  },
}));

export default PriceListTable;
